import { formatNumber } from "sipli-fleet-core/utils/formatNumber";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Skeleton, Statistic } from "antd";
import dayjs from "dayjs";
import type { Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";
import { Navigate } from "react-router-dom";
import { CustomCard } from "src/components/ui";
import CustomButton from "sipli-fleet-core/components/CustomSharedButton/CustomSharedButton";
import CustomSharedTitleSection from "sipli-fleet-core/components/CustomSharedTitleSection/CustomSharedTitleSection";
import CustomSharedDatePicker from "sipli-fleet-core/components/CustomSharedDatePicker/CustomSharedDatePicker";
import { getVehicleStateCounters } from "src/services/vehicleStateCounterService";
import emptyCounterIcon from "src/assets/svgs/vehicle-counter-empty.svg?url";
import { VEHICLE_STATES } from "src/constants/vehicleStates";
import { canViewVehicleStateCounter } from "./vehicleStateCounterAccess";

const CounterContent = () => {
  const { t, i18n } = useTranslation();
  const [selected, setSelected] = useState<Dayjs | null>(() => dayjs());
  const [timestamp, setTimestamp] = useState<string>();
  const query = useQuery({
    queryKey: ["vehicle-state-counter", timestamp],
    queryFn: ({ signal }) => getVehicleStateCounters(timestamp!, signal),
    enabled: Boolean(timestamp),
    retry: false,
    refetchOnWindowFocus: false,
  });
  const submit = () => {
    if (!selected?.isValid() || query.isFetching) return;
    const nextTimestamp = selected.toISOString();
    if (nextTimestamp === timestamp) void query.refetch();
    else setTimestamp(nextTimestamp);
  };

  return (
    <section className="min-h-full bg-gray1 px-2 pb-8">
      <CustomSharedTitleSection
        titleKey="vehicle_state_counter"
        showBtn={false}
        classNameContainer="!pt-7 !pb-8"
        titleClassName="!text-[25.23px] !font-[700] !leading-[28px]"
      />
      <form
        className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end"
        onSubmit={(event) => { event.preventDefault(); submit(); }}
      >
        <CustomSharedDatePicker
          label={t("vehicle_counter_datetime")}
          id="vehicle-counter-datetime"
          aria-label={t("vehicle_counter_datetime")}
          containerClassName="w-full sm:w-[280px]"
          className="!h-8 !rounded-[4px]"
          showTime={{ format: "HH:mm:ss" }}
          format="YYYY-MM-DD HH:mm:ss"
          value={selected}
          onChange={(value: Dayjs | null) => setSelected(value)}
          disabled={query.isFetching}
        />
        <div className="flex gap-2 self-start sm:self-auto">
          <CustomButton htmlType="submit" type="primary" buttonSize="medium"
            className="!px-[14px] !text-gray2 !leading-[18px]"
            disabled={!selected?.isValid() || query.isFetching}
            loading={query.isFetching}
            label={t("search")} />
          <CustomButton type="secondary" buttonSize="medium" label={t("reset")}
            disabled={query.isFetching || (!selected && !timestamp)}
            onClick={() => { setSelected(null); setTimestamp(undefined); }} />
        </div>
      </form>
      <section className="mt-8" aria-live="polite" aria-busy={query.isFetching}>
        {query.isFetching ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {VEHICLE_STATES.map(({ state }) => <CustomCard key={state}><Skeleton active paragraph={{ rows: 2 }} /></CustomCard>)}
          </div>
        ) : !query.isError && query.data?.length ? (
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {VEHICLE_STATES.map(({ state, key, className }) => (
                <CustomCard key={state}>
                  <span className={`inline-flex items-center rounded-md border px-3 py-1 text-base font-semibold leading-6 ${className}`}>
                    {t(key)}
                  </span>
                  <div className="mt-4">
                    <Statistic title={t("vehicle_counter_vehicles")}
                      formatter={(value) => formatNumber(value, i18n.language)}
                      value={query.data.filter((item) => item.state === state).reduce((sum, item) => sum + item.counter, 0)} />
                  </div>
                </CustomCard>
              ))}
            </div>
          </div>
        ) : (
          <div className="flex min-h-[360px] flex-col items-center justify-center rounded-[16px] bg-white px-6 py-12 text-center">
            <img src={emptyCounterIcon} alt="" className="mb-8 h-24 w-28" />
            <h3 className="mb-2 text-xl font-semibold text-black">{t("no_data_found")}</h3>
            <p className="m-0 max-w-md text-lg leading-7 text-gray9">
              {t("vehicle_counter_no_results")}<br />
              {t("vehicle_counter_change_filters")}
            </p>
          </div>
        )}
      </section>
    </section>
  );
};

const VehicleStateCounterPage = () => canViewVehicleStateCounter()
  ? <CounterContent />
  : <Navigate to="/" replace />;

export default VehicleStateCounterPage;
